import { ServiceConfigItemTypes } from '../../common/constants'
import type { ConfigItem } from '../../common/types'
import { absenceOf } from '../params/schema/field-plan'
import { type AnyZod, deriveWidget, inspect, type Inspected, shapeEntries } from '../params/schema/introspect'
import { labelOf } from '../params/schema/labels'
import { FlowExtensionParamWidgetTypes } from '../params/widget-types'
import type { SharedExtensionMetaInfoArg, SharedExtensionParam } from './model-types'

// One declared field ↔ one entry of the v1 output model. Every mapping the runner performs on a single zod
// field lives here; ./params-model.ts iterates a `Params`' declared shape and collects what these return.
//
// Where a field's DISPLAY metadata (label / uiComponent / dictionary / dependsOn) ends up depends on its
// depth, matching the v1 jsdoc-parser layout: a top-level param carries structure only and publishes its
// display side through `metaInfo.args` (→ fieldToArg), while a nested property inlines both. That is the
// single `nested` flag below — the two used to be near-duplicate functions.
//
// Display metadata is published EXACTLY as the author wrote it. What a bare `z.email()` should show when the
// author wrote no label and no description is decided when the field is RENDERED, from the schema the
// definition now carries (see ../params/schema/defaults.ts) — so those defaults can be reworded without
// every service having to be redeployed to pick the change up.

// The description as the console should show it: the author's, or nothing. An empty string is meaningful —
// it says "the author documented nothing here", which is what lets the renderer supply its own default.
const descriptionOf = (schema: AnyZod): string =>
  inspect(schema).meta.description || ''

/**
 * Whether the console should mark the field mandatory — asked of the SAME probe the runtime resolves absence
 * with (see ../params/schema/field-plan.ts), not of the wrapper chain.
 *
 * They are two ways of asking one question, and only one of them is authoritative: the runtime decides what
 * it accepts by parsing, so publishing anything else lets the form demand a value the runner would have let
 * through, or leave optional one it will reject. Reading the same answer is what keeps the two from drifting.
 */
const isRequired = (schema: AnyZod): boolean =>
  absenceOf(schema).absence === 'required'

// Whether the field is one of the platform's own param groups — an object that arrived with a widget token
// on it, which only ../params/files-options.ts produces.
const isParamGroup = (ins: Inspected): boolean =>
  ins.coreType === 'object' && !!ins.meta.frWidget

export interface FieldToParamOptions {
  nested?: boolean
}

export function fieldToParam(name: string, schema: AnyZod, options: FieldToParamOptions = {}): SharedExtensionParam {
  const ins = inspect(schema)
  const widget = deriveWidget(ins)

  const param: SharedExtensionParam = {
    name,
    required: isRequired(schema),
    description: descriptionOf(schema),
    type: widget.paramType,
  }

  /**
   * A platform param GROUP names itself on the param, at every depth.
   *
   * It is the one piece of `uiComponent` that is not display metadata derived from the field's type — the
   * rule below, which sends a top-level param's display side to `metaInfo.args`, is about how ONE CONTROL
   * should look. This says what the param IS: the runtime's own file-options object rather than an anonymous
   * `z.object` a service happened to declare (see ../params/files-options.ts). A consumer that walks
   * `bodyParams` alone can act on it, and it survives being nested inside another object.
   *
   * It is published through `metaInfo.args` as well, by `fieldToArg` below, because that is where the flow
   * designer reads a top-level param's `uiComponent` from.
   */
  if (isParamGroup(ins) && widget.uiComponent) {
    param.uiComponent = widget.uiComponent
  }

  if (options.nested) {
    param.label = labelOf(name, schema)

    if (widget.uiComponent) {
      param.uiComponent = widget.uiComponent
    }

    if (ins.meta.frDictionary) {
      param.dictionary = ins.meta.frDictionary.id

      if (ins.meta.frDictionary.dependsOn.length) {
        param.dependsOn = ins.meta.frDictionary.dependsOn
      }
    }
  }

  if (ins.defaultValue !== undefined) {
    param.defaultValue = ins.defaultValue
  }

  if (widget.items) {
    param.items = widget.items
  }

  // Only a z.object HAS declared properties. A record publishes as OBJECT too, but its keys are open — there
  // is no field list to publish, and an empty one would read as "this object has no fields".
  if (ins.coreType === 'object') {
    param.properties = propertiesOf(ins.core)
  }

  /**
   * A LIST of objects publishes the ELEMENT's fields as the param's own `properties`, next to `items`.
   *
   * That looks redundant next to `items.type` until you see what reads it: the console decides a list is a
   * repeatable row editor rather than a single expression box by testing `paramDefinition.properties` (see
   * flow-builder's `getAvailableStructureTypes`), and then renders one input per entry in it. With only
   * `items: { type: 'OBJECT' }` to go on it has no fields to draw and falls back to one text box — the
   * author's declared structure is simply invisible.
   *
   * The legacy JSDoc format has always published it this way, so this is the v2 model matching a contract
   * the console already implements rather than a new one.
   */
  if (widget.paramType === 'LIST' && widget.items?.type === 'OBJECT') {
    param.properties = propertiesOf(elementOf(ins.core))
  }

  return param
}

// The element schema of a declared array, past its own modifiers.
function elementOf(arrayCore: AnyZod | undefined): AnyZod | undefined {
  const element = arrayCore?._zod?.def?.element as AnyZod | undefined

  return element ? inspect(element).core : undefined
}

// The nested properties of an OBJECT field — its core schema's own shape, each mapped in nested form.
export function propertiesOf(objectCore: AnyZod | undefined): SharedExtensionParam[] {
  return shapeEntries(objectCore).map(([name, schema]) => fieldToParam(name, schema, { nested: true }))
}

// The display half of a top-level param: what `metaInfo.args[name]` carries.
export function fieldToArg(name: string, schema: AnyZod): SharedExtensionMetaInfoArg {
  const ins = inspect(schema)
  const arg: SharedExtensionMetaInfoArg = { label: labelOf(name, schema) }
  // A LIST's display metadata describes ONE ITEM, not the list: the console renders a repeatable editor and
  // draws each entry with this control. So `z.array(z.enum([…]))` advertises the element's dropdown, and
  // `z.array(z.string().dictionary(ref))` advertises the element's dictionary — which is what the console
  // has always expected here, and what makes a list of choices a list of choices rather than a text box.
  const display = ins.coreType === 'array' ? elementDisplay(ins) : ins
  const widget = deriveWidget(display)

  if (display.meta.frDictionary) {
    arg.dictionary = display.meta.frDictionary.id

    if (display.meta.frDictionary.dependsOn.length) {
      arg.dependsOn = display.meta.frDictionary.dependsOn
    }
  }

  if (widget.uiComponent) {
    arg.uiComponent = widget.uiComponent
  }

  return arg
}

/**
 * What a list should ADVERTISE: its element's declaration, falling back to the list's own.
 *
 * The fallback matters for `z.array(z.string()).dictionary(ref)` — binding the list rather than the element
 * reads just as naturally to an author, and both forms mean the same thing.
 */
function elementDisplay(list: Inspected): Inspected {
  const element = list.core?._zod?.def?.element as AnyZod | undefined
  const inspected = element ? inspect(element) : undefined

  if (!inspected) {
    return list
  }

  return inspected.meta.frDictionary || deriveWidget(inspected).uiComponent
    ? inspected
    : { ...inspected, meta: { ...inspected.meta, ...list.meta } }
}

// ── config items ─────────────────────────────────────────────────────────────

function toConfigType(coreType: string, frWidget?: string): ConfigItem['type'] {
  if (coreType === 'boolean') {
    return ServiceConfigItemTypes.BOOL
  }

  if (coreType === 'date') {
    return ServiceConfigItemTypes.DATE
  }

  if (coreType === 'enum') {
    return ServiceConfigItemTypes.CHOICE
  }

  if (frWidget === FlowExtensionParamWidgetTypes.MULTI_LINE_TEXT) {
    return ServiceConfigItemTypes.TEXT
  }

  return ServiceConfigItemTypes.STRING
}

// A config field → the v1 `ConfigItem` the console renders. `.shared()` marks it global rather than per-app.
export function fieldToConfigItem(name: string, schema: AnyZod): ConfigItem {
  const ins = inspect(schema)

  return {
    name,
    type: toConfigType(ins.coreType, ins.meta.frWidget),
    shared: !!ins.meta.frShared,
    required: isRequired(schema),
    description: descriptionOf(schema),
    label: labelOf(name, schema),
    defaultValue: ins.defaultValue,
    options: ins.coreType === 'enum' ? ((ins.core.options as string[]) ?? []) : [],
  }
}

